/**
 * Add someone to FPA, the LOS invite flow: pick the person from identity's directory (type-ahead)
 * or, when they are not there (or search is unavailable), enter name and company email by hand.
 * The API provisions them in identity (`fpa:view`) and stores their FPA role; they sign in with
 * Microsoft.
 */
import { type ChangeEvent, type FormEvent, useCallback, useState } from 'react'

import { errorMessage } from '@/api/client'
import type { AccessRole, AccessUser, DirectoryEntry, Me } from '@/api/types'
import { ICONS } from '@/components/icons/icons'
import { Modal } from '@/components/ui/Modal'
import { useDebouncedValue } from '@/lib/useDebouncedValue'

import styles from './Access.module.css'
import { ACCESS_EMAIL_DOMAIN, assignableRoles, companyEmailError, ROLE_HELP, ROLE_LABEL } from './rules'
import { DIRECTORY_MIN_QUERY, useAddUser, useDirectorySearch } from './useAccess'

const SEARCH_DEBOUNCE_MS = 250

function PersonOption({ person, onPick }: { person: DirectoryEntry; onPick: (person: DirectoryEntry) => void }) {
  const handleClick = useCallback(() => onPick(person), [onPick, person])
  const active = person.user_status === 'active'
  return (
    <li>
      <button type="button" className={styles.option} onClick={handleClick} disabled={active}>
        <span className={styles.optionText}>
          <span className={styles.name}>{person.name}</span>
          <span className={styles.email}>{person.email}</span>
        </span>
        {active ? <span className={styles.optionNote}>Already has access</span> : null}
        {person.user_status === 'disabled' ? (
          <span className={styles.optionNote}>Removed — will be restored</span>
        ) : null}
      </button>
    </li>
  )
}

interface AddPersonDialogProps {
  me: Me
  onClose: () => void
  onAdded: (user: AccessUser) => void
}

export function AddPersonDialog({ me, onClose, onAdded }: AddPersonDialogProps) {
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<DirectoryEntry | null>(null)
  const [manual, setManual] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [role, setRole] = useState<AccessRole>('viewer')
  const add = useAddUser()

  const term = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS)
  const search = useDirectorySearch(term)
  const unavailable = search.data?.available === false
  const mode = picked ? 'person' : manual || unavailable ? 'form' : 'search'
  const searched = term.length >= DIRECTORY_MIN_QUERY && query.trim().length >= DIRECTORY_MIN_QUERY
  const people = searched ? (search.data?.items ?? []) : []

  const emailError = companyEmailError(email)
  const showEmailError = mode === 'form' && emailTouched && emailError !== null
  const ready = mode === 'person' ? picked !== null : name.trim() !== '' && emailError === null

  const handleQuery = useCallback((event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value), [])
  const handleName = useCallback((event: ChangeEvent<HTMLInputElement>) => setName(event.target.value), [])
  const handleEmail = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    if (event.target.value.includes('@')) setEmailTouched(true)
  }, [])
  const handleEmailBlur = useCallback(() => setEmailTouched(true), [])
  const handleRole = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => setRole(event.target.value as AccessRole),
    [],
  )
  const handlePick = useCallback((person: DirectoryEntry) => setPicked(person), [])
  const handleManual = useCallback(() => setManual(true), [])
  const handleBack = useCallback(() => {
    setPicked(null)
    setManual(false)
  }, [])

  const { mutate } = add
  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setEmailTouched(true)
      if (!ready) return
      const input = picked
        ? { email: picked.email, name: picked.name, role }
        : { email: email.trim(), name: name.trim(), role }
      mutate(input, { onSuccess: onAdded })
    },
    [ready, picked, email, name, role, mutate, onAdded],
  )

  return (
    <Modal title="Add person" subtitle="They sign in to FP&A with Microsoft." onClose={onClose} size="sm">
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {mode === 'search' ? (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="access-search">
              Person
            </label>
            <input
              id="access-search"
              className={styles.input}
              type="search"
              value={query}
              onChange={handleQuery}
              placeholder="Search by name or email"
              autoComplete="off"
              data-autofocus
            />
            <span className={styles.hint}>
              {search.isFetching
                ? 'Searching the RBI directory…'
                : `Type at least ${DIRECTORY_MIN_QUERY} characters. Only @${ACCESS_EMAIL_DOMAIN} people are listed.`}
            </span>
            {searched && !search.isFetching ? (
              <ul className={styles.results} aria-label="People found">
                {people.map((person) => (
                  <PersonOption key={person.person_id} person={person} onPick={handlePick} />
                ))}
                <li>
                  <button type="button" className={styles.option} onClick={handleManual}>
                    <span className={styles.optionText}>
                      <span className={styles.name}>Add someone not in the directory</span>
                      <span className={styles.email}>
                        {people.length === 0 ? `No one matches “${term}”. ` : ''}Enter their name and email.
                      </span>
                    </span>
                  </button>
                </li>
              </ul>
            ) : null}
            {search.error ? (
              <span className={styles.fieldError} role="alert">
                {errorMessage(search.error, 'Directory search failed.')}{' '}
                <button type="button" className={styles.linkButton} onClick={handleManual}>
                  Enter details instead
                </button>
              </span>
            ) : null}
          </div>
        ) : null}

        {mode === 'person' && picked ? (
          <div className={styles.picked}>
            <span className={styles.optionText}>
              <span className={styles.name}>{picked.name}</span>
              <span className={styles.email}>{picked.email}</span>
            </span>
            <button type="button" className={styles.linkButton} onClick={handleBack}>
              Change
            </button>
          </div>
        ) : null}

        {mode === 'form' ? (
          <>
            {unavailable ? (
              <p className={styles.hint}>Directory search is not available here. Enter the person’s details.</p>
            ) : (
              <button type="button" className={styles.linkButton} onClick={handleBack}>
                Back to search
              </button>
            )}
            <div className={styles.field}>
              <label className={styles.label} htmlFor="access-name">
                Full name
              </label>
              <input
                id="access-name"
                className={styles.input}
                value={name}
                onChange={handleName}
                autoComplete="off"
                data-autofocus
                required
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="access-email">
                Company email
              </label>
              <input
                id="access-email"
                className={styles.input}
                type="email"
                value={email}
                onChange={handleEmail}
                onBlur={handleEmailBlur}
                placeholder={`name@${ACCESS_EMAIL_DOMAIN}`}
                autoComplete="off"
                aria-invalid={showEmailError}
                aria-describedby="access-email-help"
                required
              />
              <span id="access-email-help" className={showEmailError ? styles.fieldError : styles.hint}>
                {showEmailError ? emailError : `Their @${ACCESS_EMAIL_DOMAIN} Microsoft account.`}
              </span>
            </div>
          </>
        ) : null}

        {mode !== 'search' ? (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="access-role">
              Role
            </label>
            <select id="access-role" className={styles.select} value={role} onChange={handleRole}>
              {assignableRoles(me).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
            <span className={styles.hint}>{ROLE_HELP[role]}</span>
          </div>
        ) : null}

        {add.error ? (
          <div className={styles.dialogError} role="alert">
            <ICONS.alert size={14} className={styles.dialogErrorIcon} />
            {errorMessage(add.error, 'Could not add this person. Try again.')}
          </div>
        ) : null}

        <div className={styles.footer}>
          <button type="button" className={styles.secondary} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className={styles.primary} disabled={mode === 'search' || add.isPending}>
            <ICONS.plus size={14} />
            {add.isPending ? 'Adding…' : 'Add person'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
